'use_strict';

// This file has scripting for
// 1) New (2019) Aramid guarantee registration forms ( used in Central European countries ).
//    (Old version used much of the Hakka Guarantee codebase, now separated. Old Aramid is not in use anymore.)
// 2) Nokian Tyres Guarantee registration form, which is currently only available in Austria
// 3) Hakka Guarantee in Germany. This is the latest addition (June 2019).
// Hakka Guarantee in Germany uses the much of the same js functions and form fields as Aramid and Nokian Tyres guarantees and
// all of these forms use the new Salesforce integration.
// That's why these German Hakka Guarantee functions are here and not in hakkaturva.js where the (old) Nordic Hakka Guarantee is


import FormHelper from './../form-token-refresh/form-helper';
import { TelInputElement } from './../intl-tel-input/Intl-tel-input';

// Cascading form selects: select value change updates next select options.
// country -> guarantee_tyre_model -> guarantee_rim_size -> guarantee_tyre_id

export type GuaranteeFormOptions = {
  debug?: boolean;
};

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

class GuaranteeForm {
  #supportedTypes = ['aramid', 'nt_guarantee', 'hakka_guarantee_v2', 'campaign'];
  #storageKeys = {
    aramid: 'clear-aramid-guarantee-registration-form',
    nt_guarantee: 'clear-nt-guarantee-registration-form',
    hakka_guarantee_v2: 'clear-hakka-guarantee-v2-registration-form',
    campaign: 'clear-campaign-registration-form',
  };
  #type: string;
  #dom: {
    form: HTMLFormElement;
    tyreModel: HTMLSelectElement;
    rimSize: HTMLSelectElement;
    tyreId: HTMLSelectElement;
    country: HTMLSelectElement;
  };
  #submitting: boolean = false;
  #debug: boolean = false; // Prints information to console.
  #formHelper: typeof FormHelper | null = null;

  constructor(tyreModelField: HTMLSelectElement, params: GuaranteeFormOptions = {}) {

    if(params.debug) {
      this.#debug = true;
    }

    // Save references to dom elements
    const form = tyreModelField.closest('form') as HTMLFormElement;

    if(!form) {
      throw new Error('[GUARANTEE FORM] Form not found');
    }

    this.#dom = {
      tyreModel: tyreModelField,
      form: form,
      rimSize: form.querySelector('select[name="guarantee_rim_size"]') as HTMLSelectElement,
      tyreId: form.querySelector('select[name="guarantee_tyre_id"]') as HTMLSelectElement,
      country: form.querySelector('select[name="country"]') as HTMLSelectElement,
    };

    let formType = '';
    this.#supportedTypes.forEach(type => {
      if((this.#dom.form.querySelector(`input[type="hidden"][name="${type}"]`) as HTMLInputElement)?.value === '1') {
        formType = type;
      }
    });

    if(!formType) {
      throw new Error('[GUARANTEE FORM] Type not found');
    }

    this.#type = formType;

    // If token has failed, force user to reload page without form resubmit. This effectively resets form.
    if (this.#dom.form.classList.contains('stage-process-error')) {
      this.handleProcessError();
    }

    // Check if storage contains info about successful submit. If so, empty form and storage.
    this.clearFormAfterSuccessfulSubmit(this.#storageKeys[this.#type]);

    // Add event listeners
    this.#dom.tyreModel.addEventListener('change', this.onTyreModelChange.bind(this));
    this.#dom.rimSize.addEventListener('change', this.onRimSizeChange.bind(this));
    this.#dom.tyreId.addEventListener('change', this.onTyreIdChange.bind(this));
    this.#dom.country.addEventListener('change', this.onCountryChange.bind(this));

    // In case we returned to form with Stage-errors, check required fields
    Array.from(this.#dom.form.querySelectorAll('[required]')).forEach(field => {
      field.addEventListener('change keypress', () => {
        this.clearFieldErrors(field as FormField);
      });
    });

    // Use form helper to refresh token before submit
    this.#formHelper = new FormHelper(this.#dom.form, {
      requestSubmit: true,
    }) as any;

    this.#dom.form.addEventListener('submit', (e) => {
      if (e.defaultPrevented) {
        return;
      }

      e.preventDefault();
      this.onFormSubmit();
    });

    // Browser does not support requestSubmit, so just redo it manually
    this.#dom.form.addEventListener('formhelper.requestSubmit', (e) => {
      this.onFormSubmit();
    });

    this.log('initialised', this.#type);
  }

  /**
   * Creates a reload button if form process failed. Most likely a token error.
   */
  handleProcessError() {
    //TODO: translate
    const markup = '<div class="error" style="padding: 0.5em; color:red; font-size: 2em;" >FORM ERROR! Please try again.<br><a class="reload" href="#">Reset form</a></div>';
    this.#dom.form.insertAdjacentHTML('beforeend', markup);
    const formContent = this.#dom.form.querySelector('section');
    if(formContent) {
      formContent.style.display = 'none';
    }

    this.#dom.form.addEventListener('click', function(e) {
      if((e.target as HTMLElement).closest('a.reload')) {
        e.preventDefault();
        window.location.reload();
      }
    });
  }

  /**
   * Clear form when user returns to form after a successful submit.
   * Dealers need this, they submit the forms multiple times and form values get stored to browser. Autocomplete=off does not fix everything.
   * This method searches for a specific key from sessionStorage.
   * If the key is found, then 1) the form is reset by JS 2) key is removed from storage
   *
   * @param {string} storageKey - Key to search for in sessionStorage. storageKey is set by inline script when user is shown the thank-you text.
   */
  clearFormAfterSuccessfulSubmit(storageKey) {
    if(window.sessionStorage && storageKey && window.sessionStorage.getItem(storageKey)) {
      this.log('Resetting form');
      window.sessionStorage.removeItem(storageKey);
      this.#dom.form.reset();
    }
  }

  /**
   * Performs a fetch request, serializes url params.
   * Expects response to be json.
   */
  #request(url:string, params:object): Promise<any> {
    const query = Object.keys(params).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key])).join('&');
    const urlWithParams = `${url}?${query}`;
    return fetch(urlWithParams)
      .then(response => response.json());
  }

  /**
   * Clears error classes from form field, label and containing element.
   *
   * @param {HTMLelement} field - Form field
   */
  clearFieldErrors(field: FormField) {
    if(field.value) {
      field.classList.remove('stage-error');
    }
  }

  /**
   * Handles country select change
   *
   * @param {object} event - Select change event object
   */
  onCountryChange(event: Event) {
    this.log('onCountryChange', event);

    // old integration must use old country code which may be complete nonsense like "EN" which is a language.
    // That value comes from this selection element
    // But for new integration there are 2 new values that come from this same select element:
    // - country_iso
    // - region
    // These values come from db `aramid_countries`, here: https://nokiantyres.stage.crasman.fi/admin/content/database/aramid_countries/show/
    // When user changes country, update form's hidden fields that contain these new fields
    const selectedOption = this.#dom.country.querySelector('option:checked') as HTMLOptionElement;
    const countryISO = selectedOption?.dataset.countryCodeIso || '';
    const region = selectedOption?.dataset.regionCode || '';
    const countryName = selectedOption?.dataset.countryName || '';
    const stageLang = selectedOption?.dataset.stageLang || '';
    const hiddenCountryISOfield = this.#dom.form.querySelector('input[type="hidden"][name="country_iso"]') as HTMLInputElement;
    const hiddenRegionField = this.#dom.form.querySelector('input[type="hidden"][name="region"]') as HTMLInputElement;
    const hiddenCountryNameField = this.#dom.form.querySelector('input[type="hidden"][name="country_name"]') as HTMLInputElement;

    // Update hidden field values
    if(hiddenCountryISOfield) {
      hiddenCountryISOfield.value = countryISO;
    }

    if(hiddenRegionField) {
      hiddenRegionField.value = region;
    }

    if(hiddenCountryNameField) {
      hiddenCountryNameField.value = countryName;
    }

    // All sites may not have the same product selection of Aramid Guarantee products.
    // If selected option has data-stage-lang defined, fetch product list with ajax using selected stageLang
    // Data-stage-lang is defined in db `aramid_countries`
    if(this.#type === 'aramid' && stageLang) {

      // Disable and clear cascading selects
      [this.#dom.tyreModel, this.#dom.rimSize, this.#dom.tyreId].forEach(select => {
        if(select) {
          select.disabled = true;
          select.innerHTML = '<option></option>';
        }
      });

      // Fetch tyre models
      this.#request('/stage-ajax/aramid-options/', {
        lang: stageLang,
      })
        .then(data => {
          this.log('Tyre models', data);
          // enable + add options to model dropdown.
          if(this.#dom.tyreModel) {
            this.#dom.tyreModel.disabled = false;
            this.#dom.tyreModel.innerHTML = '<option></option>' + data.options;
          }
        })
        .catch(error => {
          console.error(error);
          //TODO: translate
          if(this.#dom.tyreModel) {
            this.#dom.tyreModel.innerHTML = '<option value="">Error in loading products.</option>';
          }
        });
    }

    // Change telephone field's country:
    // Are there any input fields with phone internationalization plugin attached? ( check src/js/modules/intl-tel-input.js for more )
    // If so, change plugin's country selection to match form's "country" dropdown value. This is mainly for user convenience, one choice should be enough.
    // We need to store and send guarantee phone numbers in international format(customer's request)
    const inputs = Array.from(this.#dom.form?.querySelectorAll('input.js-phone-input') || []) as HTMLInputElement[];
    if(inputs.length && countryISO) {
      inputs.forEach(input => {
        const instance = (input as TelInputElement).intlTelInputInstance;
        if(instance) {
          instance.setCountry(countryISO);
        }
      });
    }
  }

  /**
   * Handles tyre model select change
   *
   * @param {object} event - Select change event object
   */
  onTyreModelChange(event) {
    this.log('onTyreModelChange', event);

    const hiddenModelField = this.#dom.form?.querySelector('input[type="hidden"][name="tyre_model_id"]') as HTMLInputElement;
    const hiddenSizeField = this.#dom.form?.querySelector('input[type="hidden"][name="tyre_size_id"]') as HTMLInputElement;
    const hiddenSizeStringField = this.#dom.form?.querySelector('input[type="hidden"][name="tyre_size_string"]') as HTMLInputElement;
    const selectedModel = (this.#dom.tyreModel?.querySelector('option:checked') as HTMLOptionElement)?.dataset?.tyreId || '';
    const guarantee = this.#dom.tyreModel?.dataset.guarantee;
    const language = document.documentElement.lang;

    // Update hidden tyre model field value
    if(hiddenModelField) {
      hiddenModelField.value = selectedModel;
    }

    // Clear options from cascading selects
    [this.#dom.rimSize, this.#dom.tyreId].forEach(select => {
      if(select) {
        select.disabled = true;
        select.innerHTML = '<option></option>';
      }
    });

    // Reset hidden fields
    if(hiddenSizeField) {
      hiddenSizeField.value = '';
    }
    if(hiddenSizeStringField) {
      hiddenSizeStringField.value = '';
    }

    // Fetch new sizes
    this.#request('/stage-ajax/guarantee-tyre-sizes/', {
      id: selectedModel,
      guarantee: guarantee,
      language: language,
    })
      .then(data => {
        // Expect data to be {13: [{}, {}], 14: [{}, {}]}
        this.log('Tyre sizes', data);
        // Store ajax data to select element
        this.#dom.tyreModel.dataset.sizes = JSON.stringify(data);

        // Populate new data
        Object.keys(data).forEach(rim => {
          const rimOption = document.createElement('option');
          rimOption.innerText = `${rim}"`;
          rimOption.value = rim;
          rimOption.dataset.sizes = JSON.stringify(data[rim]);
          this.#dom.rimSize.append(rimOption);
        });

        this.#dom.rimSize.disabled = false;
      });
  }

  /**
   * Handles rim size select change
   *
   * @param {object} event - Select change event object
   */
  onRimSizeChange(event) {
    this.log('onRimSizeChange', event);

    const selectedOption = this.#dom.rimSize.querySelector('option:checked') as HTMLOptionElement;
    const sizes = JSON.parse(selectedOption?.dataset?.sizes || '[]');
    const hiddenSizeField = this.#dom.form.querySelector('input[type="hidden"][name="tyre_size_id"]') as HTMLInputElement;
    const hiddenSizeStringField = this.#dom.form.querySelector('input[type="hidden"][name="tyre_size_string"]') as HTMLInputElement;

    // Clear options from cascading selects
    this.#dom.tyreId.disabled = true;
    this.#dom.tyreId.innerHTML = '<option></option>';

    // Clear hidden fields
    if(hiddenSizeField) {
      hiddenSizeField.value = '';
    }
    if(hiddenSizeStringField) {
      hiddenSizeStringField.value = '';
    }

    if(sizes) {
      sizes.forEach(size => {
        const sizeOption = document.createElement('option');
        sizeOption.innerText = size.info;
        sizeOption.value = size.tid;
        sizeOption.dataset.legacyString = size.legacyString;
        this.#dom.tyreId.append(sizeOption);
      });
      this.#dom.tyreId.disabled = false;
    }

    // Remove error classes if this has value
    this.clearFieldErrors(this.#dom.rimSize);
  }

  /**
   * Handles tyre id select change
   *
   * @param {object} event - Select change event object
   */
  onTyreIdChange(event) {
    this.log('onTyreIdChange', event);

    const selectedOption = this.#dom.tyreId.querySelector('option:checked') as HTMLOptionElement;
    const hiddenSizeField = this.#dom.form.querySelector('input[type="hidden"][name="tyre_size_id"]') as HTMLInputElement;
    const hiddenSizeStringField = this.#dom.form.querySelector('input[type="hidden"][name="tyre_size_string"]') as HTMLInputElement;
    const value = this.#dom.tyreId.value;
    const legacyString = selectedOption?.dataset?.legacyString;

    // Set selected tyre-size-id (tid) to a hidden field
    if(hiddenSizeField) {
      hiddenSizeField.value = value || '';
    }
    // Set the legacy tyre size string to a hidden field. Note that this is a different string than the one visible in select element:
    // Select element now contains full descriptive text like "LT235/85R16 120/116Q Studded". This is the legacy version of that same string: "235/85R16"
    // Since the legacy string format may be in use @ salesforce, we send it through integration.
    if(hiddenSizeStringField) {
      hiddenSizeStringField.value = legacyString || '';
    }

    // Remove error classes if this has value
    this.clearFieldErrors(this.#dom.tyreId);
  }

  /**
   * Handles form validation and submitting.
   *
   * @returns {boolean}
   */
  onFormSubmit() {
    this.log('onFormSubmit', {submitting: this.#submitting});

    // Prevent double clicks
    if(this.#submitting) {
      return false;
    } else {
      this.#submitting = true;
      const submitBtn = this.#dom.form.querySelector('[type="submit"]') as HTMLButtonElement;

      // Check errors
      const errorEls = Array.from(this.#dom.form.querySelectorAll('.stage-error'));
      if(errorEls.length) {
        errorEls[0].scrollIntoView({behavior: 'smooth', block: 'center'});
        this.#submitting = false;

        // Attach change event listeners to input fields with errors to clear errors
        errorEls.forEach((errorEl) => {
          const inputField = errorEl as HTMLInputElement;
          const clearErrorHandler = () => {
            // Clear the error
            inputField.classList.remove('stage-error'); // Replace 'error-class' with the actual class used for errors
            inputField.removeAttribute('aria-invalid');
            inputField.removeAttribute('aria-errormessage');

            // Remove the error message element if it exists
            const inputContainer = inputField.closest('.form__input-container') as HTMLElement;
            const errorMessageElement = inputContainer?.querySelector('.form__error-message') as HTMLElement;
            if (errorMessageElement) {
              errorMessageElement.innerText = '';
              errorMessageElement.classList.add('hidden');
            }

            // Remove the event listener
            inputField.removeEventListener('change', clearErrorHandler);
          };

          // Attach the event listener
          inputField.addEventListener('change', clearErrorHandler);
        });

        return false;
      // In nt_guarantee form, check additional fields
      } else if (this.#type === 'nt_guarantee') {
        const emailField = this.#dom.form.querySelector('[name="email"]') as HTMLInputElement;
        const phoneField = this.#dom.form.querySelector('[name="phone"]') as HTMLInputElement;

        // Either email or phone required
        if(emailField.value === '' && phoneField.value === '') {
          const translation = window?.translations?.error__nt_guarantee__phone_or_email || 'Either the phone number or email address is required.';
          const errorParagraph = `<p class="ntg-error form__error-message">${translation}</p>`;

          if(!this.#dom.form.querySelector('.ntg-error')) {
            submitBtn.insertAdjacentHTML('beforebegin', errorParagraph);
          }

          this.#submitting = false;
          return false;
        }
      }

      this.#submitting = false;
      this.#dom.form.submit();
    }
  }

  /**
   * Logs information to console
   */
  log(...args: any[]) {
    if(this.#debug) {
      console.log('[GUARANTEE FORM]', ...arguments);
    }
  }
}

export default GuaranteeForm;

/**
 * Initialses guarantee form functionality based on fields on page.
 */
export function init(selectField: HTMLSelectElement) {
  new GuaranteeForm(selectField, {debug: false});
}
